import { ref, reactive, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

export function useFormAnalytics(formId, formType = 'guardian-angel') {
  // Analytics state
  const sessionId = ref(generateSessionId());
  const startTime = ref(Date.now());
  const analytics = reactive({
    formId,
    formType,
    sessionId: sessionId.value,
    startTime: startTime.value,
    fieldInteractions: {},
    validationErrors: {},
    currentStep: 1,
    totalSteps: 1,
    abandonmentPoint: null,
    timeSpent: 0,
    isCompleted: false,
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    referrer: document.referrer
  });

  // Track field interactions
  const trackFieldInteraction = (fieldName, action, value = null, metadata = {}) => {
    const timestamp = Date.now();
    
    if (!analytics.fieldInteractions[fieldName]) {
      analytics.fieldInteractions[fieldName] = {
        fieldName,
        firstInteraction: timestamp,
        interactions: [],
        timeSpent: 0,
        validationErrors: 0,
        finalValue: null,
        completed: false
      };
    }

    const field = analytics.fieldInteractions[fieldName];
    
    // Calculate time spent if this is a blur event
    if (action === 'blur' && field.interactions.length > 0) {
      const lastFocus = field.interactions
        .slice()
        .reverse()
        .find(i => i.action === 'focus');
      
      if (lastFocus) {
        const timeSpentThisSession = timestamp - lastFocus.timestamp;
        field.timeSpent += timeSpentThisSession;
      }
    }

    // Track the interaction
    field.interactions.push({
      action, // focus, blur, input, change, validation_error, etc.
      timestamp,
      value: value ? String(value).substring(0, 100) : null, // Truncate for privacy
      valueLength: value ? String(value).length : 0,
      metadata
    });

    // Update final value and completion status
    if (value !== null && value !== undefined && value !== '') {
      field.finalValue = String(value).substring(0, 100);
      field.completed = true;
    }

    // Send interaction data periodically
    debouncedSendAnalytics();
  };

  // Track validation errors
  const trackValidationError = (fieldName, errorMessage, errorType = 'validation') => {
    const timestamp = Date.now();
    
    if (!analytics.validationErrors[fieldName]) {
      analytics.validationErrors[fieldName] = [];
    }

    analytics.validationErrors[fieldName].push({
      timestamp,
      errorMessage: errorMessage.substring(0, 200),
      errorType,
      attemptNumber: analytics.validationErrors[fieldName].length + 1
    });

    // Also track as field interaction
    trackFieldInteraction(fieldName, 'validation_error', null, {
      errorMessage: errorMessage.substring(0, 200),
      errorType
    });

    // Update field validation error count
    if (analytics.fieldInteractions[fieldName]) {
      analytics.fieldInteractions[fieldName].validationErrors++;
    }
  };

  // Track step progression
  const trackStepChange = (step, totalSteps = null) => {
    analytics.currentStep = step;
    if (totalSteps) {
      analytics.totalSteps = totalSteps;
    }

    trackFormEvent('step_change', {
      step,
      totalSteps: analytics.totalSteps,
      timeToReachStep: Date.now() - startTime.value
    });
  };

  // Track form events (start, abandon, complete, etc.)
  const trackFormEvent = (eventType, metadata = {}) => {
    const timestamp = Date.now();
    analytics.timeSpent = timestamp - startTime.value;

    const eventData = {
      ...analytics,
      eventType,
      timestamp,
      metadata,
      fieldInteractions: Object.values(analytics.fieldInteractions),
      validationErrors: analytics.validationErrors
    };

    // Send immediately for important events
    if (['form_complete', 'form_abandon', 'form_error'].includes(eventType)) {
      sendAnalytics(eventData, true);
    } else {
      debouncedSendAnalytics(eventData);
    }
  };

  // Track form completion
  const trackFormCompletion = (success = true, metadata = {}) => {
    analytics.isCompleted = success;
    analytics.timeSpent = Date.now() - startTime.value;
    
    trackFormEvent(success ? 'form_complete' : 'form_error', {
      success,
      totalTimeSpent: analytics.timeSpent,
      fieldsCompleted: Object.values(analytics.fieldInteractions)
        .filter(f => f.completed).length,
      totalFields: Object.keys(analytics.fieldInteractions).length,
      ...metadata
    });
  };

  // Track form abandonment
  const trackFormAbandonment = (reason = 'unknown') => {
    analytics.abandonmentPoint = {
      step: analytics.currentStep,
      lastField: getLastActiveField(),
      reason,
      timeSpent: Date.now() - startTime.value,
      fieldsCompleted: Object.values(analytics.fieldInteractions)
        .filter(f => f.completed).length
    };

    trackFormEvent('form_abandon', {
      reason,
      abandonmentPoint: analytics.abandonmentPoint
    });
  };

  // Get the last field the user interacted with
  const getLastActiveField = () => {
    let lastField = null;
    let lastTimestamp = 0;

    Object.values(analytics.fieldInteractions).forEach(field => {
      const lastInteraction = field.interactions[field.interactions.length - 1];
      if (lastInteraction && lastInteraction.timestamp > lastTimestamp) {
        lastTimestamp = lastInteraction.timestamp;
        lastField = field.fieldName;
      }
    });

    return lastField;
  };

  // Generate unique session ID
  function generateSessionId() {
    return 'ga_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Debounced analytics sending
  let analyticsTimeout = null;
  const debouncedSendAnalytics = (data = null) => {
    clearTimeout(analyticsTimeout);
    analyticsTimeout = setTimeout(() => {
      sendAnalytics(data || analytics);
    }, 2000); // Send every 2 seconds when there's activity
  };

  // Send analytics data to server
  const sendAnalytics = async (data = null, immediate = false) => {
    try {
      const payload = data || {
        ...analytics,
        timeSpent: Date.now() - startTime.value,
        fieldInteractions: Object.values(analytics.fieldInteractions),
        validationErrors: analytics.validationErrors
      };

      await axios.post('/api/form-analytics', payload, {
        timeout: immediate ? 10000 : 5000
      });
    } catch (error) {
      // Silently fail for analytics - don't interrupt user experience
      console.warn('Form analytics submission failed:', error.message);
    }
  };

  // Handle page unload (form abandonment)
  const handlePageUnload = () => {
    if (!analytics.isCompleted) {
      trackFormAbandonment('page_unload');
    }
  };

  // Handle visibility change (tab switching)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      trackFormEvent('tab_hidden', {
        timeSpent: Date.now() - startTime.value
      });
    } else {
      trackFormEvent('tab_visible', {
        timeSpent: Date.now() - startTime.value
      });
    }
  };

  // Setup event listeners
  onMounted(() => {
    window.addEventListener('beforeunload', handlePageUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Track form start
    trackFormEvent('form_start', {
      url: window.location.href,
      referrer: document.referrer,
      timestamp: startTime.value
    });
  });

  // Cleanup
  onUnmounted(() => {
    window.removeEventListener('beforeunload', handlePageUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    clearTimeout(analyticsTimeout);
    
    // Final analytics send if not completed
    if (!analytics.isCompleted) {
      trackFormAbandonment('component_unmount');
    }
  });

  return {
    trackFieldInteraction,
    trackValidationError,
    trackStepChange,
    trackFormEvent,
    trackFormCompletion,
    trackFormAbandonment,
    getAnalytics: () => analytics,
    sessionId: sessionId.value
  };
} 